import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {SelectItem} from "primeng/api";

import {CommonTransformationService} from "./common-transformation.service";
import {Transaction} from '../enums/txn-type.enum';
import {PoolType} from "../enums/pool-type.enum";
import {LabelChange} from "@app/shared/classes/label-change";
import {UserLookupDetails, UserSummary} from "@app/shared/classes/user";
import {CostPool, QueryResult, UserPhoto, UserType} from "@app/shared";
import {BusinessClassification} from "@app/shared/classes/business-classification";
import {SystemParam} from "@app/solution-manager/system/system-param/system-param";
import {TxnStructure} from "@app/main/worklist/worklist";
import {OrgInfo} from "@app/shared/classes/org-info";

@Injectable()
export class CommonDataService {
  public countryList: SelectItem[];
  public stateList: SelectItem[];

  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  public getParameterByName(name: string, url: string) {
    if (!url) {
      url = window.location.href;
    }
    name = name.replace(/[\[\]]/g, "\\$&");
    const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
      results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return "";
    }
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  // common utility services: getEnum, getSysParams, getUser, getUserById, getUserByName, getPhotoById

  getEnumValue(name: string): Observable<string[]> {
    const url = `/utils/enum-values?name=${name}`;
    return this.http.get<string[]>(url);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  getEnumMembers(name: string, property?: string): Observable<Map<string, Object>[]> {
    let url = `/utils/enum-members?name=${name}`;
    if (property) {
      url += `&property=${property}`;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    return this.http.get<Map<string, Object>[]>(url);
  }

  getEnumValueAsDropdown(name: string, noTitleCase?: boolean, noTitleCaseList?: string[]): Observable<SelectItem[]> {
    const url = `/utils/enum-values?name=${name}`;
    return this.http.get<any>(url).pipe(map(res => {
      return this.transform.convertArrayToDropDown(res, noTitleCase, noTitleCaseList);
    }));
  }

  getEnumPairAsDropdown(name: string, noTitleCase?: boolean, noTitleCaseList?: string[], labelOrValueToExclude?: string[], noSort?: boolean, labelChangeList?: LabelChange[]): Observable<SelectItem[]> {
    const url = "/utils/enum-pairs?name=" + name + "&start=0&count=-1";
    return this.http.get<Map<string, string>[]>(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res, "id", noTitleCase, noTitleCaseList, labelOrValueToExclude, noSort, labelChangeList);
    }));
  }

  getSysParams(name: string): Observable<SystemParam> {
    const url = "/utils/sys-params/" + name;
    return this.http.get<SystemParam>(url);
  }

  getTeamByName(teamName: string): Observable<QueryResult> {
    const url = `/utils/team-lookup/name-search?name=${teamName}`;
    return this.http.get<QueryResult>(url);
  }

  // returns UserLookupDetails
  getUser(): Observable<UserLookupDetails> {
    const url = "/utils/user-lookup/self";
    return this.http.get<UserLookupDetails>(url);
  }

  getUserById(userId: number): Observable<UserSummary> {
    const url = `/utils/user-lookup?userId=${userId}`;
    return this.http.get<UserSummary>(url);
  }

  searchUserByName(userName: string, subtype?: UserType, obsId?: number, inParentObs?: boolean): Observable<QueryResult> {
    let url = `/utils/user-lookup/name-search?name=${userName}`;
    if (subtype) {
      url += `&subtype=${subtype}`;
    }
    if (obsId) {
      url += `&obsId=${obsId}`;
    }
    if (inParentObs) {
      url += `&inParentObs=${String(inParentObs)}`;
    }
    return this.http.get<QueryResult>(url);
  }

  searchUserByNameAsDropDown(userName: string, subtype?: UserType, obsId?: number, inParentObs?: boolean): Observable<SelectItem[]> {
    return this.searchUserByName(userName, subtype, obsId, inParentObs).pipe(map(res => {
      return this.transform.convertToDropDown("fullName", res.list, "id", true);
    }));
  }

  getRecruiterByName(userName: string): Observable<QueryResult> {
    const url = `/recruiting/recruiters/search?name=${userName}`;
    return this.http.get<QueryResult>(url);
  }

  getPhotoByUserId(userId: number): Observable<UserPhoto> {
    const url = `/hr/users/${userId}/photo`;
    return this.http.get<UserPhoto>(url);
  }

  getTxnTypeIdByName(txnTypeName: Transaction): Observable<Map<string, string>> {
    const url = "/utils/look-up/txn-type-id?transaction=" + txnTypeName;
    return this.http.get<Map<string, string>>(url);
  }

  getStatusByTxnType(txnTypeName: Transaction): Observable<Map<string, string>[]> {
    const url = "/utils/look-up/status?transaction=" + txnTypeName;
    return this.http.get<Map<string, string>[]>(url);
  }

  getStatusByTxnTypeAsDropDown(txnTypeName: Transaction): Observable<SelectItem[]> {
    return this.getStatusByTxnType(txnTypeName).pipe(map(res => {
      return this.transform.convertToDropDown("name", res, "name");
    }));
  }

  getCostPool(type?: PoolType): Observable<CostPool[]> {
    let url = `/finance/cost-pool?count=-1`;
    if (type) {
      url += `&type=${type}`;
    }
    return this.http.get<CostPool[]>(url);
  }

  getCostPoolAsDropdown(type?: PoolType, returnObjValue?: boolean): Observable<SelectItem[]> {
    if (returnObjValue) {
      return this.getCostPool(type).pipe(map(res => {
        return this.transform.convertToDropDown("name", res);
      }));
    } else {
      return this.getCostPool(type).pipe(map(res => {
        return this.transform.convertToDropDown("name", res, "id");
      }));
    }
  }

  getRefDomainsAsDropdown(returnObjValue?: boolean): Observable<SelectItem[]> {
    const url = "/lookup/domains";

    if (returnObjValue) {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list);
      }));
    } else {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id");
      }));
    }
  }

  getRefCodes(refDomainName: string): Observable<QueryResult> {
    const url = `/lookup/domains/codes?name=${refDomainName}&start=0&count=-1`;
    return this.http.get<QueryResult>(url);
  }

  /**
   * @desc retrieved ref code from the backend and return them in a dropdown menu
   */
  getRefCodeAsDropdown(refDomainName: string, returnName?: boolean, returnObj?: boolean, titleCase?: boolean, label?: string, labels?: string[]): Observable<SelectItem[]> {
    if (returnObj) {
      if (label || labels) {
        return this.getRefCodes(refDomainName).pipe(map(res => {
          if (labels) {
            return this.transform.convertToLongLabelDropDown(labels, res.list, null, !titleCase);
          } else {
            return this.transform.convertToDropDown(label, res.list, null, !titleCase);
          }
        }));
      } else {
        return this.getRefCodes(refDomainName).pipe(map(res => {
          return this.transform.convertToDropDown("name", res.list, null, !titleCase);
        }));
      }
    } else if (returnName) {
      return this.getRefCodes(refDomainName).pipe(map(res => {
        if (label || labels) {
          if (labels) {
            return this.transform.convertToLongLabelDropDown(labels, res.list, "name", !titleCase);
          } else {
            return this.transform.convertToDropDown(label, res.list, "name", !titleCase);
          }
        } else {
          return this.transform.convertToDropDown("name", res.list, "name", !titleCase);
        }
      }));
    } else {
      return this.getRefCodes(refDomainName).pipe(map(res => {
        if (label || labels) {
          if (labels) {
            return this.transform.convertToLongLabelDropDown(labels, res.list, "id", !titleCase);
          } else {
            return this.transform.convertToDropDown(label, res.list, "id", !titleCase);
          }
        } else {
          return this.transform.convertToDropDown("name", res.list, "id", !titleCase);
        }
      }));
    }
  }

  getCodeLongLabelAsDropDown(codeName: string, labelProperties: string[], valueProperty?: string, titleCase?: boolean): Observable<SelectItem[]> {
    const url = "/lookup/domains/codes?name=" + codeName + "&start=0&count=-1";
    return this.http.get<QueryResult>(url).pipe(map(res => {
      return this.transform.convertToLongLabelDropDown(labelProperties, res.list, valueProperty, titleCase);
    }));
  }

  searchCodeByDesc(codeName: string, desc: string): Observable<QueryResult> {
    const url = "/lookup/domains/codes/search?name=" + codeName + "&desc=" + desc;
    return this.http.get<QueryResult>(url);
  }

  getComplianceAgenciesAsDropDown(labelProperties: string[], valueProperty?: string, titleCase?: boolean): Observable<SelectItem[]> {
    const url = "/core/compliance-agencies/search";
    return this.http.get<QueryResult>(url).pipe(map(res => {
      return this.transform.convertToLongLabelDropDown(labelProperties, res.list, valueProperty, titleCase);
    }));
  }

  // ToDo: move connect services to somewhere else
  setLastLogForSelf(action: string): Observable<Response> {
    const url = "/connect/log-time?userAction=" + action;

    return this.http.post<Response>(url, {});
  }

  getLastLogForSelf(action: string): Observable<Date> {
    const url = "/connect/log-time/" + action;

    return this.http.get<Date>(url);
  }

  getAllLocations(): Observable<QueryResult> {
    const url = '/directory/locations';
    return this.http.get<QueryResult>(url);
  }

  getAllLocationsAsDropdown(): Observable<SelectItem[]> {
    return this.getAllLocations().pipe(map(res => {
      return this.transform.convertToDropDown("addrName", res.list, "id");
    }));
  }

  // approval run-time support services - get top-level transaction category
  getTxnStructureTypes(idOnly?: boolean): Observable<SelectItem<TxnStructure>[]> {
    const url = "/approval/worklist/txn-structures";

    if (idOnly) {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return <SelectItem<TxnStructure>[]>this.transform.convertToDropDown("name", res.list, "id", true);
      }));
    } else {
      return this.http.get<QueryResult>(url).pipe(map(res => {
        return <SelectItem<TxnStructure>[]>this.transform.convertToDropDown("name", res.list, null, true);
      }));
    }
  }

  // approval run-time support services - get transaction types
  getTxnNames(txnStructId?: number): Observable<SelectItem[]> {
    let url = "/approval/worklist/txn-names";
    if (txnStructId) {
      url += `?txnStructureId=${txnStructId}`;
    }
    return this.http.get<QueryResult>(url).pipe(map(res => {
      return this.transform.convertArrayToDropDown(res.list);
    }));
  }

  // approval run-time support services - get status
  getStatusTypes(): Observable<SelectItem[]> {
    const url = "/approval/worklist/status";
    return this.http.get<QueryResult>(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res.list, null, true);
    }));
  }

  getAllTransactionStatuses(txnName: string): Observable<SelectItem[]> {
    const url = '/utils/look-up/status?transaction=' + txnName;
    return this.http.get<Map<string, string>[]>(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res, "id");
    }));
  }

  getWorklistCount(type: string): Observable<QueryResult> {
    const url = `/approval/worklist/status-counts?type=${type}`;
    return this.http.get<QueryResult>(url);
  }

  getBusinessClassificationsAsDropdown(): Observable<SelectItem[]> {
    const url = '/ach/business-classifications';
    return this.http.get<BusinessClassification[]>(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res, null, true);
    }));
  }

  getMyTasks(): Observable<QueryResult> {
    const url = `/todo/my-tasks`;
    return this.http.get<QueryResult>(url);
  }

  getSubgOrgs(): Observable<OrgInfo[]> {
    const url = `/utils/sub-orgs`;
    return this.http.get<OrgInfo[]>(url);
  }
}
